import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import BackToDashboard from "@/components/BackToDashboard";
import {
  Plus,
  X,
  Trash2,
  ImagePlus,
  ImagePlus as BulkImageIcon,
  Sparkles,
  ArrowUpRight,
  Lock,
  Package,
  Ruler,
  PencilLine,
  Check,
  AlertTriangle,
  BookOpen,
  Copy,
  CheckCircle2,
  Download,
  Star,
  ChevronDown,
} from "lucide-react";
import api from "@/lib/api";

/**
 * Catalog.jsx
 * ------------------------------------------------------------------
 * Drop-in page for the CheckoutDM merchant dashboard.
 * - Wired to the live catalog API (GET/POST/PUT/DELETE /api/catalog).
 * - Picking a photo opens the edit modal in "create" mode immediately —
 *   nothing is saved to the server until the merchant hits Save, since
 *   the backend requires name + price + image together in one call.
 * - `plan` / `country` come from the real merchant via props (see
 *   pages/CatalogPage.jsx). `limit`/`used` come from the server
 *   response, not a hardcoded table, so it always matches plan_limits.
 * - Products render in rows of 8, "Show more" reveals the next 8.
 * - Uploads are restricted to JPG, max 1 MB (also enforced server-side).
 * - Pro plan can add up to 3 extra photos per product, shown on the
 *   public storefront gallery only — DM checkout still uses the main
 *   photo (image) exclusively, unchanged.
 * - Gender + Color (Starter and above) power DM search filters like
 *   "men black jeans" — optional, same gating as Category.
 * - Gated features (Category/Gender/Color, Low Stock Alerts, extra
 *   photos, bulk tools) are never fully hidden — merchants below the
 *   required plan see a locked preview with an upgrade nudge instead,
 *   so everyone can see what they're missing. See <LockedFeature>.
 * ------------------------------------------------------------------
 */

const MAX_IMAGE_BYTES = 1 * 1024 * 1024; // 1 MB
const ALLOWED_IMAGE_TYPE = "image/jpeg";
const MAX_ADDITIONAL_IMAGES = 3;
const MAX_BESTSELLERS = 3;
const MAX_LINK_LABEL = 20;
const MAX_LINK_URL = 2000;

// Returns an error message, or "" if the link is fine (blank is fine — it's optional).
const validateHttpsLink = (raw, fieldName) => {
  const v = (raw || "").trim();
  if (!v) return "";
  if (/\s/.test(v)) return `${fieldName} can't contain spaces.`;
  if (v.length > MAX_LINK_URL) return `${fieldName} is too long.`;
  let u;
  try {
    u = new URL(v);
  } catch (e) {
    return `${fieldName} isn't a valid link.`;
  }
  if (u.protocol !== "https:") return `${fieldName} must start with https://`;
  if (!u.hostname.includes(".")) return `${fieldName} isn't a valid link.`;
  return "";
};

const PLAN_LIMITS = {
  free: 5,
  creator: 150,
  starter: 500,
  growth: 1000,
  pro: 10000,
};

const PLAN_LABEL = {
  free: "Free",
  creator: "Creator",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
};

const PAGE_SIZE = 8;

// Maps a backend catalog row (see GET /api/catalog) into the shape this
// page already uses internally. Backend already returns this shape, but
// keeping the mapper explicit makes it obvious what the contract is.
const fromApiItem = (row) => ({
  id: row.id,
  image: row.image,
  title: row.title,
  price: row.price,
  qty: row.qty,
  stock: row.stock || [],
  sold_out: !!row.sold_out,
  sizes: row.sizes || [],
  desc: row.desc || "",
  is_digital: row.is_digital,
  digital_delivery_url: row.digital_delivery_url,
  active: row.active !== false,
  additional_images: row.additional_image_urls || [],
  weight_grams: row.weight_grams ?? null,
  length_cm: row.length_cm ?? null,
  width_cm: row.width_cm ?? null,
  height_cm: row.height_cm ?? null,
  category: row.category || "",
  gender: row.gender || "",
  color: row.color || "",
  is_bestseller: !!row.is_bestseller,
  preview_url: row.preview_url || "",
  external_url: row.external_url || "",
  external_label: row.external_label || "",
  dm_action: row.dm_action || "select",
});

// ------------------------------------------------------------------------
export default function Catalog({ plan = "free", country = "IN" }) {
  const navigate = useNavigate();  
  const [products, setProducts] = useState([]);
  const categoryOptions = useMemo(() => collectDistinct(products.map((p) => p.category)), [products]);
  const colorOptions = useMemo(() => collectDistinct(products.map((p) => p.color)), [products]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(PLAN_LIMITS[plan] ?? PLAN_LIMITS.free);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [editing, setEditing] = useState(null); // product object, or draft {id: null, ...} for create
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [toast, setToast] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const fileRef = useRef(null);
  const bulkImageRef = useRef(null);

  const currency = country === "IN" ? "INR" : "USD";
  const currencySymbol = currency === "INR" ? "₹" : "$";
  const formatPrice = (n) =>
    currency === "INR"
      ? `₹${Number(n || 0).toLocaleString("en-IN")}`
      : `$${Number(n || 0).toLocaleString("en-US")}`;

  const used = products.length;
  const remaining = Math.max(0, limit - used);
  const atCap = used >= limit;
  const utilisation = Math.min(100, Math.round((used / limit) * 100));

  // Feature gates — every merchant sees the feature; only merchants below
  // the required plan see it locked with an upgrade nudge (see
  // <LockedFeature>). Keep this the single source of truth for the gates
  // so the toolbar, the modal, and any future surfaces stay in sync.
  const categoryLocked = plan === "free"; // Category/Gender/Color — Starter+
  const lowStockLocked = plan === "free" || plan === "creator" || plan === "starter"; // Low stock alerts — Growth+
  const additionalImageSlots = plan === "pro" ? 3 : plan === "growth" ? 2 : plan === "starter" ? 1 : 0;
  const additionalImagesLocked = additionalImageSlots === 0; // Extra gallery photos — none on Free, tiered 1/2/3 above
  const bulkToolsLocked = plan !== "pro"; // Bulk photo upload + CSV export — Pro only
  const bestsellerLocked = plan !== "pro"; // Bestseller pin — Pro only, max 3
  const linkOutLocked = plan === "free"; // Preview link & Buy/affiliate link — Creator and above.
  const bestsellerCount = products.filter((p) => p.is_bestseller).length;

  const flash = (msg, tone = "mint") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 2200);
  };

  const apiErrorMessage = (err, fallback) => {
    const code = err?.response?.data?.error;
    if (code === "catalog_limit_reached") return "You've hit your plan's product limit. Upgrade to add more.";
    if (code === "bestseller_limit_reached") return "You can only pin 3 bestsellers. Untag one first.";
    if (code === "unsupported_image_type") return "Only JPG images are allowed.";
    if (code === "image_too_large") return "Image is over 1 MB — please compress it and try again.";
    if (code === "image_required") return "Please choose a photo.";
    if (code === "name_required") return "Product title is required.";
    if (code === "title_too_long") return "Product title is way too long — please shorten it.";
    if (code === "valid_price_required") return "Enter a valid price.";
    if (code === "free_requires_digital") return "Only digital products on a paid plan can be free (price 0).";
    if (code === "creator_digital_only") return "The Creator plan supports digital products only.";
    if (code === "invalid_preview_url") return "Preview link isn't a valid https:// link.";
    if (code === "invalid_buy_url") return "Buy link isn't a valid https:// link.";
    if (code === "preview_and_buy_conflict") return "Use either a Preview link or a Buy link, not both.";
    if (code === "link_only_requires_buy_link") return "'Link only' needs a Buy link. Add one, or switch back to Select.";
    if (code === "instant_requires_free_digital") return "'Instant free access' needs a digital product priced at 0 with a delivery link.";
    if (code === "invalid_dm_action") return "Invalid tap action.";
    if (code === "digital_requires_delivery_link") return "Add a delivery link for this digital product.";
    return err?.response?.data?.message || fallback;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await api.get("/catalog");
        if (cancelled) return;
        setProducts((data.items || []).map(fromApiItem));
        if (typeof data.limit === "number") setLimit(data.limit);
      } catch (err) {
        if (!cancelled) flash(apiErrorMessage(err, "Couldn't load your catalog. Try refreshing."), "hotpink");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validateFile = (f) => {
    if (f.type !== ALLOWED_IMAGE_TYPE) {
      flash("Only JPG images are allowed.", "hotpink");
      return false;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      flash("Image must be under 1 MB. Please compress it and try again.", "hotpink");
      return false;
    }
    return true;
  };

  // Picking a photo opens the edit modal in "create" mode with nothing
  // saved yet — the actual POST happens on Save, once name + price are filled in.
  const onPickFiles = (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    if (atCap) {
      flash(`No slots left on ${PLAN_LABEL[plan] ?? plan}. Upgrade to add more.`, "hotpink");
      return;
    }
    const file = list[0];
    if (!validateFile(file)) return;
    setEditing({
      id: null,
      image: URL.createObjectURL(file),
      _imageFile: file,
      title: "",
      price: "",
      qty: "",
      weight_grams: "",
      length_cm: "",
      width_cm: "",
      height_cm: "",
      sizes: [],
      desc: "",
      category: "",
      gender: "",
      color: "",
      additional_images: [],
      is_bestseller: false,
    });
  };

  const removeProduct = async (id) => {
    const prev = products;
    setProducts((p) => p.filter((x) => x.id !== id));
    setConfirmDelete(null);
    try {
      await api.delete(`/catalog?id=${id}`);   
      flash("Product removed.", "peach");
    } catch (err) {
      setProducts(prev); // roll back on failure
      flash(apiErrorMessage(err, "Couldn't remove that product. Try again."), "hotpink");
    }
  };

  const saveProduct = async (form) => {
    const isCreate = !form.id;
    const fd = new FormData();
    if (!isCreate) fd.append("id", form.id);   
    fd.append("name", form.title.trim() || "Untitled product");
    fd.append("price", String(Number(form.price) || 0));
    fd.append("quantity", form.qty === null || form.qty === undefined ? "" : String(form.qty));
    fd.append("weight_grams", form.weight_grams === null || form.weight_grams === undefined ? "" : String(form.weight_grams));
    fd.append("length_cm", form.length_cm === null || form.length_cm === undefined ? "" : String(form.length_cm));
    fd.append("width_cm", form.width_cm === null || form.width_cm === undefined ? "" : String(form.width_cm));
    fd.append("height_cm", form.height_cm === null || form.height_cm === undefined ? "" : String(form.height_cm));
    fd.append("stock", JSON.stringify(form.stock || []));
    fd.append("description", (form.desc || "").slice(0, 200));
    fd.append("sizes", (form.sizes || []).join(", "));
    fd.append("is_digital", form.is_digital ? "true" : "false");
    fd.append("digital_delivery_url", form.is_digital ? (form.digital_delivery_url || "").trim() : "");
    // Starter/Growth/Pro — powers category browsing in DMs (reply CATEGORIES).
    // Public catalog page category filtering is still pending backend/frontend work.
    fd.append("category", (form.category || "").trim().slice(0, 60));
    // Starter/Growth/Pro — powers gender/color filters in DM search (e.g. "men black jeans").
    fd.append("gender", form.gender || "");
    fd.append("color", (form.color || "").trim().slice(0, 40));
    // Pro-only: pins this product to the top of the dashboard grid and the
    // public storefront, plus shows a Bestseller badge. Capped at 3 per
    // merchant — the modal already blocks a 4th, this is belt-and-suspenders.
    fd.append("is_bestseller", plan === "pro" && form.is_bestseller ? "true" : "false");
    // Optional DM card links. Blank clears them.
    fd.append("preview_url", form.preview_url || "");
    fd.append("external_url", form.external_url || "");
    fd.append("external_label", form.external_label || "");
    fd.append("dm_action", form.dm_action || "select");
    if (form._imageFile) fd.append("image", form._imageFile);

    // Pro-only: up to 3 extra photos. Each of the 3 slots is sent as EITHER
    // a new file (additional_image_N) or the existing URL to keep
    // (additional_image_N_existing_url) — never both. A slot the merchant
    // cleared sends neither, which the backend should read as "remove it".
    // This lets the merchant reorder/replace/remove any single slot without
    // re-uploading the other two.
    if (additionalImageSlots > 0) {
      (form.additional_images || []).slice(0, additionalImageSlots).forEach((slot, i) => {
        const n = i + 1;
        if (slot?.file) {
          fd.append(`additional_image_${n}`, slot.file);
        } else if (slot?.url) {
          fd.append(`additional_image_${n}_existing_url`, slot.url);
        }
      });
    }
    try {
      const { data } = isCreate
        ? await api.post("/catalog", fd)
        : await api.put("/catalog", fd);
      const saved = fromApiItem(data);
      setProducts((p) => (isCreate ? [saved, ...p] : p.map((x) => (x.id === saved.id ? saved : x))));
      setEditing(null);
      flash(isCreate ? "Product added." : "Product saved.");
    } catch (err) {
      flash(apiErrorMessage(err, "Couldn't save that product. Try again."), "hotpink");
    }
  };

  // Bestsellers pinned first, stable order otherwise — since `shown` is
  // just a client-side slice of the already-fetched list (no server
  // pagination), sorting here is enough; no extra API call needed.
  const bestsellerPinOn = plan === "pro";
  const shown = [...products]
    .sort((a, b) => ((bestsellerPinOn && b.is_bestseller) ? 1 : 0) - ((bestsellerPinOn && a.is_bestseller) ? 1 : 0))
    .slice(0, visible);

  return (
    <section className="min-h-screen bg-cream px-6 py-12 md:px-14 md:py-16">
      {/* Header */}
      <div className="mx-auto max-w-[1400px]">
        <BackToDashboard />
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-hotpink">Catalog</p>
        <h1 className="mt-3 max-w-3xl text-4xl font-black tracking-tight text-ink md:text-6xl">
          Your storefront, ready for the next DM.
        </h1>
        <p className="mt-4 max-w-xl text-ink/60">
          Drop images, add a title, price, quantity, and inventory. Set once and let us handle the rest.
        </p>
        <Link
          to="/catalog-guide"
          className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-hotpink hover:underline"
        >
          <BookOpen size={13} /> Not sure how Catalog works? See the full guide →
        </Link>
        {plan === "pro" && (
          <Link
            to="/bulk-image-upload-guide"
            className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-hotpink hover:underline"
          >
            <BookOpen size={13} /> How does bulk photo upload work? See the guide →
          </Link>
        )}

        {/* Plan / usage strip */}
        <div className="mt-10 flex flex-col gap-4 rounded-3xl border border-ink/10 bg-white p-6 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-full bg-ink px-3 py-1 text-xs font-semibold uppercase tracking-widest text-cream">
              <Sparkles className="h-3.5 w-3.5" />
              {PLAN_LABEL[plan] ?? plan} plan
            </span>
            <span className="text-sm text-ink/60">
              {used.toLocaleString("en-IN")} / {limit.toLocaleString("en-IN")} products
            </span>
          </div>

          <div className="flex-1 md:mx-8">
            <div className="h-2 overflow-hidden rounded-full bg-ink/5">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${utilisation}%` }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className={`h-full rounded-full ${atCap ? "bg-hotpink" : "bg-gradient-to-r from-hotpink via-red-500 to-orange-400"}`}
              />
            </div>
          </div>

          {atCap ? (
            <button
              data-testid="catalog-upgrade-btn"
              onClick={() => navigate("/plans")}
              className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-hotpink via-red-500 to-orange-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm"
            >
              Upgrade plan <ArrowUpRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              data-testid="catalog-add-btn"
              disabled={loading}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2.5 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-50"
            >
              <Plus className="h-4 w-4" />
              Add products
            </button>
          )}

          {/* Bulk photo upload + CSV export — Pro only. Everyone else still
              sees the buttons so they know the tools exist, but tapping
              either one nudges toward upgrading instead of opening a picker. */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (bulkToolsLocked) {
                  navigate("/plans");
                  return;
                }
                (async () => {
                  const res = await api.get("/merchant/catalog/export/csv", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = "catalog.csv";
                  a.click();
                  URL.revokeObjectURL(url);
                })();
              }}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-semibold ${
                bulkToolsLocked
                  ? "border-dashed border-ink/15 text-ink/40 hover:border-hotpink hover:text-hotpink"
                  : "border-ink/15 text-ink hover:bg-ink/5"
              }`}
              data-testid="catalog-export-csv-btn"
            >
              {bulkToolsLocked ? <Lock className="h-3.5 w-3.5" /> : <Download className="h-4 w-4" />} Export CSV
              {bulkToolsLocked && <span className="rounded-full bg-ink/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink/40">Pro</span>}
            </button>
            <button
              onClick={() => {
                if (bulkToolsLocked) {
                  navigate("/plans");
                  return;
                }
                bulkImageRef.current?.click();
              }}
              disabled={bulkUploading}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2.5 text-sm font-semibold disabled:opacity-50 ${
                bulkToolsLocked
                  ? "border-dashed border-ink/15 text-ink/40 hover:border-hotpink hover:text-hotpink"
                  : "border-ink/15 text-ink hover:bg-ink/5"
              }`}
              data-testid="catalog-bulk-upload-btn"
            >
              {bulkUploading ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-ink/20 border-t-ink" />
                  Uploading…
                </>
              ) : (
                <>
                  {bulkToolsLocked ? <Lock className="h-3.5 w-3.5" /> : <BulkImageIcon className="h-4 w-4" />} Bulk upload photos
                  {bulkToolsLocked && <span className="rounded-full bg-ink/5 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink/40">Pro</span>}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Public catalog page toggle moved to its own page — see ProfileMenu "Public Catalog Page" */}

         {used > limit && (
          <div className="mt-4 rounded-2xl border border-hotpink/30 bg-hotpink/5 px-5 py-3 text-sm text-ink/80">
            You're at <strong>{used}</strong> products but your <strong>{PLAN_LABEL[plan] ?? plan}</strong> plan only
            allows <strong>{limit}</strong>. The {used - limit} newest {used - limit === 1 ? "product is" : "products are"} hidden
            from customers until you upgrade or remove some.
          </div>
        )}

        {/* Hidden native input */}
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg"
          className="hidden"
          onChange={(e) => {
            onPickFiles(e.target.files);
            e.target.value = "";
          }}
          data-testid="catalog-file-input"
        />

        {/* Hidden bulk-image input (Pro only) */}
        <input
          ref={bulkImageRef}
          type="file"
          accept="image/jpeg"
          multiple
          className="hidden"
          onChange={async (e) => {
            const files = Array.from(e.target.files || []).slice(0, 50);
            e.target.value = "";
            if (files.length === 0) return;
            setBulkUploading(true);
            flash(`Uploading ${files.length} photo${files.length === 1 ? "" : "s"}…`, "lilac");
            try {
              const images = await Promise.all(files.map(f => new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve({
                  filename: f.name,
                  mime_type: f.type,
                  data_base64: reader.result.split(",")[1],
                });
                reader.onerror = () => reject(new Error("read_failed"));
                reader.readAsDataURL(f);
              })));
              const { data } = await api.post("/catalog/bulk-images", { images });
              const drafts = (data.products || []).map((p) => ({
                id: p.id,
                image: p.image_url,
                title: "Untitled product — tap Edit to finish",
                price: 0,
                qty: 0,
                stock: [],
                sold_out: true,
                sizes: [],
                desc: "",
                is_digital: false,
                digital_delivery_url: null,
                active: true,
                additional_images: [],
              }));
              setProducts((prev) => [...drafts, ...prev]);
              flash(
                `${drafts.length} photo${drafts.length === 1 ? "" : "s"} added as new products — tap Edit on each to finish.`,
                "mint"
              );
            } catch (err) {
              const msg = err?.response?.data?.error || "Upload failed. Please try again.";
              flash(msg, "hotpink");
            } finally {
              setBulkUploading(false);
            }
          }}
          data-testid="catalog-bulk-image-input"
        />

        {/* Upload dropzone */}
        {!atCap && !loading && (
          <UploadZone onPick={onPickFiles} />
        )}

        {/* Grid */}
        <div className="mt-10">
          <div className="mb-4 flex items-baseline justify-between">
            <h2 className="text-lg font-bold text-ink">All products</h2>
            <p className="text-xs text-ink/50">
              {loading ? "Loading…" : `Showing ${shown.length} of ${products.length}`}
            </p>
          </div>

          {loading ? (
            <div className="grid h-40 place-items-center text-sm text-ink/50">Loading your catalog…</div>
          ) : (
          <>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-8">
            <AnimatePresence initial={false}>
              {shown.map((p, i) => (
                <ProductCard
                  key={p.id}
                  index={i}
                  product={p}
                  plan={plan}
                  formatPrice={formatPrice}
                  onEdit={() => setEditing({ ...p, _imageFile: null })}
                  onDelete={() => setConfirmDelete(p)}
                />
              ))}
            </AnimatePresence>
          </div>

          {products.length > visible && (
            <div className="mt-8 flex justify-center">
              <button
                data-testid="catalog-show-more"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white px-5 py-2.5 text-sm font-semibold text-ink hover:bg-ink/5"
              >
                Show next {Math.min(PAGE_SIZE, products.length - visible)} products
              </button>
            </div>
          )}
          </>
          )}
        </div>
      </div>

      {/* Edit modal */}
      <AnimatePresence>
        {editing && (
          <EditProductModal
            product={editing}
            currencySymbol={currencySymbol}
            plan={plan}
            categoryLocked={categoryLocked}
            lowStockLocked={lowStockLocked}
            additionalImagesLocked={additionalImagesLocked}
            additionalImageSlots={additionalImageSlots}
            bestsellerLocked={bestsellerLocked}
            bestsellerCount={bestsellerCount}
            linkOutLocked={linkOutLocked}
            categoryOptions={categoryOptions}
            colorOptions={colorOptions}
            onClose={() => setEditing(null)}
            onSave={saveProduct}
          />
        )}
      </AnimatePresence>

      {/* Confirm delete */}
      <AnimatePresence>
        {confirmDelete && (
          <ConfirmDialog
            title="Remove this product?"
            body={`"${confirmDelete.title}" will disappear from your catalog and any active DM checkout links.`}
            confirmLabel="Yes, remove"
            onCancel={() => setConfirmDelete(null)}
            onConfirm={() => removeProduct(confirmDelete.id)}
          />
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-ink px-5 py-3 text-sm font-semibold text-cream shadow-lg"
            data-testid="catalog-toast"
          >
            <span className={`mr-2 inline-block h-2 w-2 rounded-full bg-${toast.tone}`} />
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}

// -------------------- Locked feature wrapper ---------------------------
// Renders a compact, self-sized locked card in place of the gated
// controls — icon, one short line explaining the perk, and a one-tap
// upgrade CTA, all in a single row that wraps naturally. Deliberately NOT
// an overlay-on-top-of-blurred-content: that approach clips whenever the
// real controls underneath are shorter than the lock message, which is
// exactly what happened with the single-line Category/Gender/Color
// fields. A standalone card always sizes to its own content, so it can
// never get cut off no matter what it's replacing. When `locked` is
// false it's a no-op passthrough to the real controls.
function LockedFeature({ locked, planLabel, message, onUpgrade, className = "", children }) {
  if (!locked) return children;
  return (
    <div
      className={`w-full min-w-0 rounded-2xl border border-dashed border-ink/15 bg-ink/[0.02] px-4 py-3 ${className}`}
    >
      <div className="flex items-center gap-2">
        <span className="grid h-6 w-6 flex-none place-items-center rounded-full bg-ink/10 text-ink/50">
          <Lock className="h-3 w-3" />
        </span>
        <p className="min-w-0 flex-1 text-xs font-semibold leading-snug text-ink/60">{message}</p>
      </div>
      <button
        type="button"
        onClick={onUpgrade}
        className="mt-2 inline-flex w-full items-center justify-center gap-1 rounded-full bg-ink px-3 py-1.5 text-[11px] font-semibold text-cream hover:bg-ink/85"
      >
        Upgrade to {planLabel} <ArrowUpRight className="h-3 w-3" />
      </button>
    </div>
  );
}

// -------------------- Product card -----------------------------------------
function ProductCard({ product, onEdit, onDelete, index, formatPrice, plan }) {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.4, delay: Math.min(index, 8) * 0.03 }}
      className={`group relative overflow-hidden rounded-2xl border border-ink/10 bg-white ${product.active === false ? "opacity-60" : ""}`}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-ink/5">
        <img
          src={product.image}
          alt={product.title}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {product.active === false && (
          <span className="absolute left-2 top-2 rounded-full bg-ink/80 px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-cream">
            {plan === "creator" && !product.is_digital ? "Hidden · Creator is digital-only" : "Locked · Upgrade to show"}
          </span>
        )}
        {product.active !== false && product.sold_out && (
          <span className="absolute left-2 top-2 rounded-full bg-hotpink px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
            Sold Out · Hidden from DMs
          </span>
        )}
        {plan === "pro" && product.is_bestseller && (
          <span className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-amber-400 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
            <Star className="h-2.5 w-2.5 fill-ink" /> Bestseller
          </span>
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
        <div className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-between gap-2 p-2 transition-transform group-hover:translate-y-0">
          <button
            onClick={onEdit}
            className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-white/95 px-2.5 py-1 text-[11px] font-semibold text-ink"
            data-testid={`product-edit-${product.id}`}
          >
            <PencilLine className="h-3 w-3" /> Edit
          </button>
          <button
            onClick={onDelete}
            className="pointer-events-auto inline-flex items-center gap-1 rounded-full bg-hotpink px-2.5 py-1 text-[11px] font-semibold text-white"
            data-testid={`product-delete-${product.id}`}
          >
            <Trash2 className="h-3 w-3" /> Delete
          </button>
        </div>
      </div>
      <div className="p-3">
        <p className="line-clamp-1 text-sm font-semibold text-ink">{product.title}</p>
        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
          <span className="text-sm font-bold text-ink">{product.is_digital && Number(product.price) === 0 ? "Free" : formatPrice(product.price)}</span>
          <span className={`text-[11px] ${product.sold_out ? "font-semibold text-hotpink" : "text-ink/50"}`}>
            {product.qty === null || product.qty === undefined
              ? "unlimited"
              : product.sold_out
              ? "out of stock"
              : `${product.qty} left`}
          </span>
        </div>
        {(product.category || product.gender || product.color) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {product.category && (
              <span className="inline-block rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-semibold text-ink/50">
                {product.category}
              </span>
            )}
            {product.gender && (
              <span className="inline-block rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-semibold capitalize text-ink/50">
                {product.gender}
              </span>
            )}
            {product.color && (
              <span className="inline-block rounded-full bg-ink/5 px-2 py-0.5 text-[10px] font-semibold capitalize text-ink/50">
                {product.color}
              </span>
            )}
          </div>
        )}
        {product.sizes?.length > 0 && Array.isArray(product.stock) && product.stock.length > 0 && (
          <p className="mt-1 line-clamp-1 text-[10px] text-ink/40">
            {product.stock.map((s) => `${s.size || "—"}: ${s.quantity === null ? "∞" : s.quantity}`).join(" · ")}
          </p>
        )}
      </div>
    </motion.div>
  );
}

// -------------------- Upload zone -------------------------------------------
function UploadZone({ onPick }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        onPick(e.dataTransfer.files);
      }}
      className={`mt-6 flex cursor-pointer items-center justify-between gap-4 rounded-3xl border-2 border-dashed p-6 transition-colors ${
        drag ? "border-hotpink bg-hotpink/5" : "border-ink/15 bg-white hover:bg-ink/[0.02]"
      }`}
      data-testid="catalog-dropzone"
    >
      <div className="flex items-center gap-4">
        <div className="grid h-12 w-12 place-items-center rounded-2xl bg-mint text-ink">
          <ImagePlus className="h-6 w-6" />
        </div>
        <div>
          <p className="font-bold text-ink">Drop a product photo here</p>
          <p className="text-sm text-ink/60">
            JPG only · Up to 1 MB
          </p>
        </div>
      </div>
      <span className="hidden rounded-full border border-ink/15 bg-white px-4 py-2 text-sm font-semibold text-ink md:inline-block">
        Browse files
      </span>
      <input
        type="file"
        accept="image/jpeg"
        className="hidden"
        onChange={(e) => {
          onPick(e.target.files);
          e.target.value = "";
        }}
      />
    </label>
  );
}

// -------------------- Edit modal --------------------------------------------
function EditProductModal({ product, onClose, onSave, currencySymbol, plan, categoryLocked, lowStockLocked, additionalImagesLocked, additionalImageSlots, bestsellerLocked, bestsellerCount, linkOutLocked, categoryOptions = [], colorOptions = [] }) {
  const navigate = useNavigate();
  const goUpgrade = () => navigate("/plans");
  const isCreate = !product.id;
  const initialStockBySize = {};
  const initialThresholdBySize = {};
  (product.stock || []).forEach((s) => {
    const key = s.size || "";
    if (s.size) initialStockBySize[s.size] = s.quantity === null || s.quantity === undefined ? "" : String(s.quantity);
    if (s.low_stock_threshold !== null && s.low_stock_threshold !== undefined) {
      initialThresholdBySize[key] = String(s.low_stock_threshold);
    }
  });
  // Normalize into fixed 3-slot array: { url, file } | null per slot.
  // Existing saved URLs go in with no file; empty slots stay null.
  const initialAdditionalImages = Array.from({ length: MAX_ADDITIONAL_IMAGES }, (_, i) => {
    const url = (product.additional_images || [])[i];
    return url ? { url, file: null } : null;
  });
  const [form, setForm] = useState({
    ...product,
    is_digital: plan === "creator" ? true : product.is_digital,
    sizesStr: (product.sizes || []).join(", "),
    stockBySize: initialStockBySize,
    thresholdBySize: initialThresholdBySize,
    additional_images: initialAdditionalImages,
  });
  const currentSizes = form.sizesStr.split(",").map((s) => s.trim()).filter(Boolean);
  const setSizeStock = (size, value) =>
    setForm((f) => ({ ...f, stockBySize: { ...f.stockBySize, [size]: value } }));
  const setSizeThreshold = (size, value) =>
    setForm((f) => ({ ...f, thresholdBySize: { ...f.thresholdBySize, [size]: value } }));
  const [descLen, setDescLen] = useState((product.desc || "").length);
  const [titleLen, setTitleLen] = useState((product.title || "").length);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const modalScrollRef = useRef(null);

  const setAdditionalImageSlot = (index, slot) => {
    setForm((f) => {
      const next = [...f.additional_images];
      next[index] = slot;
      return { ...f, additional_images: next };
    });
  };

  useEffect(() => {
    if (!formError) return;
    modalScrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    const t = setTimeout(() => setFormError(null), 4000);
    return () => clearTimeout(t);
  }, [formError]);
  const imageInputRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const previewLinkErr = validateHttpsLink(form.preview_url, "Sample / demo link");
  const buyLinkErr = validateHttpsLink(form.external_url, "Buy link");
  const hasPreviewValue = !!(form.preview_url || "").trim();
  const hasBuyLinkValue = !!(form.external_url || "").trim();

  // bestsellerCount is the total across ALL of the merchant's products —
  // if this product is already one of them, don't count it against itself,
  // or a merchant editing an existing bestseller would get locked out of
  // its own toggle at exactly the cap.
  const otherBestsellerCount = product.is_bestseller ? bestsellerCount - 1 : bestsellerCount;
  const atBestsellerCap = otherBestsellerCount >= MAX_BESTSELLERS;

    const submit = async (e) => {
    e.preventDefault();
    if (isCreate && !form._imageFile) {
      setFormError("Please choose a product photo.");
      return;
    }
    if (!form.title.trim()) {
      setFormError("Product title is required.");
      return;
    }
    const priceText = String(form.price ?? "").trim();
    const priceNum = Number(priceText);
    if (plan === "creator" && !form.is_digital) {
      setFormError("The Creator plan supports digital products only.");
      return;
    }
    if (priceText === "" || !Number.isFinite(priceNum) || priceNum < 0) {
      setFormError("Enter a valid price.");
      return;
    }
    if (priceNum === 0 && (plan === "free" || !form.is_digital)) {
      setFormError("Only digital products on a paid plan can be free (price 0).");
      return;
    }
    if (
      form.is_digital &&
      !(form.digital_delivery_url || "").trim() &&
      !(!linkOutLocked && (form.dm_action || "select") === "link_only" && (form.external_url || "").trim())
    ) {
      setFormError("Add a delivery link for this digital product. (A product with only a Buy link must be set to “Link only” under “When someone taps this product”.)");
      return;
    }
    const canLinkOut = !linkOutLocked;
    if (!linkOutLocked) {
      const previewErr = validateHttpsLink(form.preview_url, "Preview link");
      if (previewErr) {
        setFormError(previewErr);
        return;
      }
    }
    if (canLinkOut && (form.preview_url || "").trim() && (form.external_url || "").trim()) {
      setFormError("Use either a Preview link or a Buy link, not both — clear one first.");
      return;
    }
    if (canLinkOut) {
      const buyErr = validateHttpsLink(form.external_url, "Buy link");
      if (buyErr) {
        setFormError(buyErr);
        return;
      }
    }
    if (!linkOutLocked) {
      const act = form.dm_action || "select";
      if (act === "link_only" && !(form.external_url || "").trim()) {
        setFormError("'Link only' needs a Buy link. Add one, or switch back to Select.");
        return;
      }
      if (act === "instant" && !(form.is_digital && String(form.price).trim() !== "" && Number(form.price) === 0 && (form.digital_delivery_url || "").trim())) {
        setFormError("'Instant free access' needs a digital product priced at 0 with a delivery link.");
        return;
      }
    }
    setFormError(null);
    const sizes = form.is_digital
      ? []
      : form.sizesStr.split(",").map((s) => s.trim()).filter(Boolean);

    // Blank = untracked/unlimited for that size (or for the whole product,
    // if it has no sizes). Anything typed in becomes the real stock count.
    const stock = sizes.length > 0
      ? sizes.map((s) => {
          const raw = form.stockBySize?.[s];
          const rawThreshold = form.thresholdBySize?.[s];
          return {
            size: s,
            quantity: raw === "" || raw === undefined || raw === null ? null : Number(raw),
            low_stock_threshold: lowStockLocked || rawThreshold === "" || rawThreshold === undefined || rawThreshold === null ? null : Number(rawThreshold),
          };
        })
      : [{
          size: "",
          // form.qty can be null here (e.g. loaded from an existing product
          // whose quantity was already "unlimited"), not just "" — treat both
          // the same way an untouched blank field would be treated.
          quantity: form.qty === "" || form.qty === null || form.qty === undefined ? null : Number(form.qty),
          low_stock_threshold: lowStockLocked || form.thresholdBySize?.[""] === "" || form.thresholdBySize?.[""] == null ? null : Number(form.thresholdBySize[""]),
        }];

    const totalQty = stock.some((s) => s.quantity === null)
      ? null
      : stock.reduce((sum, s) => sum + (s.quantity || 0), 0);

    setSaving(true);
    try {
        await onSave({
        id: form.id,
        _imageFile: form._imageFile,
        title: form.title.trim(),
        price: Number(form.price) || 0,
        qty: totalQty,
        stock,
        desc: form.desc.slice(0, 200),
        sizes,
        is_digital: !!form.is_digital,
        digital_delivery_url: form.is_digital ? (form.digital_delivery_url || "").trim() : null,
        category: categoryLocked ? "" : (form.category || "").trim().slice(0, 60),
        gender: categoryLocked || form.is_digital ? "" : (form.gender || ""),
        color: categoryLocked || form.is_digital ? "" : (form.color || "").trim().slice(0, 40),
        additional_images: additionalImagesLocked ? [] : form.additional_images,
        is_bestseller: bestsellerLocked ? false : !!form.is_bestseller,
        preview_url: linkOutLocked ? "" : (form.preview_url || "").trim(),
        external_url: linkOutLocked ? "" : (form.external_url || "").trim(),
        external_label: linkOutLocked ? "" : (form.external_label || "").trim().slice(0, MAX_LINK_LABEL),
        dm_action: linkOutLocked ? "select" : (form.dm_action || "select"),
        weight_grams:
          form.weight_grams === "" || form.weight_grams === null || form.weight_grams === undefined
            ? null
            : Number(form.weight_grams),
        length_cm:
          form.length_cm === "" || form.length_cm === null || form.length_cm === undefined ? null : Number(form.length_cm),
        width_cm:
          form.width_cm === "" || form.width_cm === null || form.width_cm === undefined ? null : Number(form.width_cm),
        height_cm:
          form.height_cm === "" || form.height_cm === null || form.height_cm === undefined ? null : Number(form.height_cm),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/60 p-0 md:items-center md:p-6"
      onClick={onClose}
      data-testid="edit-product-modal"
    >
      <motion.form
        ref={modalScrollRef}
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 260 }}
        className="max-h-[90vh] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-t-3xl bg-cream md:rounded-3xl"
      >
        <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
          <h3 className="text-lg font-bold text-ink">{isCreate ? "Add product" : "Edit product"}</h3>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-full bg-white hover:bg-ink/5"
            data-testid="edit-close"
          >
            <X className="h-4 w-4 text-ink" />
          </button>
        </div>

        <AnimatePresence>
          {formError && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden px-6"
            >
              <div className="mt-4 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 px-4 py-3">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-hotpink" />
                <p className="text-sm font-medium text-ink/80">{formError}</p>
                <button
                  type="button"
                  onClick={() => setFormError(null)}
                  className="ml-auto text-ink/40 hover:text-ink"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="grid gap-6 p-6 md:grid-cols-[220px_1fr]">
          <div>
            <div
              className="group relative aspect-square cursor-pointer overflow-hidden rounded-2xl bg-ink/5"
              onClick={() => imageInputRef.current?.click()}
              data-testid="edit-image-trigger"
            >
              <img src={form.image} alt="" className="h-full w-full object-cover" />
              <div className="absolute inset-0 grid place-items-center bg-ink/0 text-white opacity-0 transition group-hover:bg-ink/40 group-hover:opacity-100">
                <PencilLine className="h-5 w-5" />
              </div>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/jpeg"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (!f) return;
                  if (f.type !== "image/jpeg") {
                    setFormError("Only JPG images are allowed.");
                    return;
                  }
                  if (f.size > MAX_IMAGE_BYTES) {
                    setFormError("Image must be under 1 MB. Please compress it and try again.");
                    return;
                  }
                  setFormError(null);
                  set("image", URL.createObjectURL(f));
                  set("_imageFile", f);
                }}
              />
            </div>

            {/* Up to 3 extra photos shown in the public storefront gallery.
                DM checkout still only ever uses the main photo above.
                Visible to every plan; locked with an upgrade nudge below Pro. */}
            <div className="mt-3">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink/50">
                More photos <span className="normal-case text-ink/30">· optional · {additionalImageSlots || 1} extra on your plan</span>
              </p>
              <LockedFeature
                locked={additionalImagesLocked}
                planLabel="Starter"
                message="Add extra storefront photos — 1 on Starter, 2 on Growth, 3 on Pro"
                onUpgrade={goUpgrade}
              >
                <div className="grid grid-cols-3 gap-1.5">
                  {form.additional_images.map((slot, i) =>
                    i < additionalImageSlots ? (
                      <AdditionalImageSlot
                        key={i}
                        slot={slot}
                        onPick={(file) => setAdditionalImageSlot(i, { url: URL.createObjectURL(file), file })}
                        onRemove={() => setAdditionalImageSlot(i, null)}
                        onError={setFormError}
                      />
                    ) : (
                      <LockedImageSlot key={i} onUpgrade={goUpgrade} />
                    )
                  )}
                </div>
              </LockedFeature>
            </div>
          </div>

          <div className="space-y-4">
            <Field label="Product title" required hint={`${titleLen}/100`}>
              <input
                value={form.title}
                onChange={(e) => {
                  set("title", e.target.value);
                  setTitleLen(e.target.value.length);
                }}
                maxLength={100}
                className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                data-testid="edit-title"
              />
            </Field>

            <Field label="Description" hint={`${descLen}/200`}>
              <textarea
                rows={3}
                maxLength={200}
                value={form.desc}
                onChange={(e) => {
                  set("desc", e.target.value);
                  setDescLen(e.target.value.length);
                }}
                placeholder="Fabric, fit, sizing, shipping — whatever helps a buyer say yes in one DM."
                className="w-full resize-none rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                data-testid="edit-desc"
              />
            </Field>

            {!isCreate && (
            <LockedFeature
              locked={bestsellerLocked}
              planLabel="Pro"
              message="Pin your top products — shown first, with a Bestseller badge"
              onUpgrade={goUpgrade}
            >
              <Field
                label="Bestseller"
                hint={atBestsellerCap ? "Limit reached · 3 max" : "optional · up to 3 products"}
              >
                <button
                  type="button"
                  disabled={atBestsellerCap}
                  onClick={() => set("is_bestseller", !form.is_bestseller)}
                  role="switch"
                  aria-checked={!!form.is_bestseller}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                    form.is_bestseller
                      ? "border-amber-400 bg-amber-400 text-ink"
                      : "border-ink/15 bg-white text-ink hover:bg-ink/5"
                  }`}
                  data-testid="edit-bestseller-toggle"
                >
                  <Star className={`h-4 w-4 ${form.is_bestseller ? "fill-ink" : ""}`} />
                  {form.is_bestseller ? "Tagged as Bestseller" : "Tag as Bestseller"}
                </button>
              </Field>
            </LockedFeature>
            )}

            <Field label="Product type">
              <div className="flex gap-2">
                {plan !== "creator" && (
                  <button
                    type="button"
                    onClick={() => set("is_digital", false)}
                    className={`rounded-2xl border px-4 py-2.5 text-sm font-semibold ${!form.is_digital ? "border-ink bg-ink text-cream" : "border-ink/15 bg-white text-ink"}`}
                    data-testid="edit-type-physical"
                  >
                    Physical
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => set("is_digital", true)}
                  className={`rounded-2xl border px-4 py-2.5 text-sm font-semibold ${form.is_digital ? "border-ink bg-ink text-cream" : "border-ink/15 bg-white text-ink"}`}
                  data-testid="edit-type-digital"
                >
                  Digital
                </button>
              </div>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field
                label={`Price (${currencySymbol})`}
                required
                hint={form.is_digital && plan !== "free" ? "Enter 0 to give it free" : undefined}
              >
                <input
                  type="number"
                  min="0"
                  value={form.price}
                  onChange={(e) => set("price", e.target.value)}
                  placeholder={form.is_digital && plan !== "free" ? "Write 0 to give this item for free" : ""}
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                  data-testid="edit-price"
                />
              </Field>
              {currentSizes.length === 0 && (
                <Field label="Quantity in stock" hint="optional">
                  <div className="relative">
                    <Package className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                    <input
                      type="number"
                      min="0"
                      value={form.qty ?? ""}
                      onChange={(e) => set("qty", e.target.value)}
                      placeholder="Blank = unlimited"
                      className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-hotpink focus:outline-none"
                      data-testid="edit-qty"
                    />
                  </div>
                </Field>
              )}
            </div>

            {currentSizes.length === 0 && (
              <LockedFeature
                locked={lowStockLocked}
                planLabel="Growth"
                message="Get notified when stock runs low"
                onUpgrade={goUpgrade}
              >
                <Field label="Low stock alert at" hint="optional · Growth/Pro">
                  <input
                    type="number"
                    min="0"
                    value={form.thresholdBySize?.[""] ?? ""}
                    onChange={(e) => setSizeThreshold("", e.target.value)}
                    placeholder="Blank = no alert"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                    data-testid="edit-threshold"
                  />
                </Field>
              </LockedFeature>
            )}

            {form.is_digital && (
              <Field label="Delivery link" required hint="sent to buyer right after payment, on Instagram + email">
                <input
                  type="url"
                  value={form.digital_delivery_url || ""}
                  onChange={(e) => set("digital_delivery_url", e.target.value)}
                  placeholder="https://drive.google.com/... or your download/course link"
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                  data-testid="edit-digital-url"
                />
              </Field>
            )}

            <LockedFeature
              locked={categoryLocked}
              planLabel="Creator"
              message="Let customers browse by category in DMs"
              onUpgrade={goUpgrade}
            >
              <Field label="Category" hint="optional · lets customers browse by category when they DM you">
                <SuggestInput
                  noun="category"
                  nounPlural="categories"
                  value={form.category}
                  onChange={(v) => set("category", v)}
                  options={categoryOptions}
                  maxLength={60}
                  placeholder="e.g. Tops, Bottoms, Accessories"
                  testId="category-suggestions"
                  inputTestId="edit-category"
                />
              </Field>
            </LockedFeature>

            {!form.is_digital && (
            <LockedFeature
              locked={categoryLocked}
              planLabel="Starter"
              message="Filter DM search by gender & color"
              onUpgrade={goUpgrade}
            >
              <div className="grid grid-cols-2 gap-4">
                <Field label="Gender" hint="optional · improves DM search">
                  <select
                    value={form.gender || ""}
                    onChange={(e) => set("gender", e.target.value)}
                    className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                    data-testid="edit-gender"
                  >
                    <option value="">Not specified</option>
                    <option value="men">Men</option>
                    <option value="women">Women</option>
                    <option value="unisex">Unisex</option>
                    <option value="kids">Kids</option>
                  </select>
                </Field>
                <Field label="Color" hint="optional · e.g. Black">
                  <SuggestInput
                    noun="color"
                    nounPlural="colors"
                    value={form.color}
                    onChange={(v) => set("color", v)}
                    options={colorOptions}
                    maxLength={40}
                    placeholder="e.g. Black"
                    testId="color-suggestions"
                    inputTestId="edit-color"
                  />
                </Field>
              </div>
            </LockedFeature>
            )}

            {!form.is_digital && (
              <Field label="Weight (grams)" hint="optional · used for shipping rate calculation">
                <input
                  type="number"
                  min="0"
                  value={form.weight_grams}
                  onChange={(e) => set("weight_grams", e.target.value)}
                  placeholder="e.g. 250"
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                  data-testid="edit-weight-grams"
                />
              </Field>
            )}

            {!form.is_digital && (
              <Field label="Package size (cm)" hint="optional · L × W × H · blank defaults to 10×10×10cm">
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    min="0"
                    value={form.length_cm}
                    onChange={(e) => set("length_cm", e.target.value)}
                    placeholder="L"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                    data-testid="edit-length-cm"
                  />
                  <input
                    type="number"
                    min="0"
                    value={form.width_cm}
                    onChange={(e) => set("width_cm", e.target.value)}
                    placeholder="W"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                    data-testid="edit-width-cm"
                  />
                  <input
                    type="number"
                    min="0"
                    value={form.height_cm}
                    onChange={(e) => set("height_cm", e.target.value)}
                    placeholder="H"
                    className="w-full rounded-2xl border border-ink/15 bg-white px-3 py-2.5 text-sm focus:border-hotpink focus:outline-none"
                    data-testid="edit-height-cm"
                  />
                </div>
              </Field>
            )}

            {!form.is_digital && (
            <Field label="Sizes" hint="optional · comma separated">
              <div className="relative">
                <Ruler className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
                <input
                  value={form.sizesStr}
                  onChange={(e) => set("sizesStr", e.target.value)}
                  placeholder="S, M, L, XL"
                  className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-hotpink focus:outline-none"
                  data-testid="edit-sizes"
                />
              </div>
            </Field>
            )}

            {!form.is_digital && currentSizes.length > 0 && (
              <Field label="Stock per size" hint="optional">
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {currentSizes.map((size) => (
                    <div key={size} className="space-y-1.5 min-w-0">
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-ink/40">
                          {size}
                        </span>
                        <input
                          type="number"
                          min="0"
                          value={form.stockBySize?.[size] ?? ""}
                          onChange={(e) => setSizeStock(size, e.target.value)}
                          placeholder="∞"
                          className="w-full rounded-2xl border border-ink/15 bg-white py-2.5 pl-12 pr-3 text-sm focus:border-hotpink focus:outline-none"
                          data-testid={`edit-stock-${size}`}
                        />
                      </div>
                      <LockedFeature
                        locked={lowStockLocked}
                        planLabel="Growth"
                        message="Get notified when any size runs low"
                        onUpgrade={goUpgrade}
                      >
                        <div className="relative">
                          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-ink/30">
                            Alert
                          </span>
                          <input
                            type="number"
                            min="0"
                            value={form.thresholdBySize?.[size] ?? ""}
                            onChange={(e) => setSizeThreshold(size, e.target.value)}
                            placeholder="Off"
                            className="w-full rounded-2xl border border-ink/15 bg-white py-2 pl-12 pr-3 text-xs focus:border-hotpink focus:outline-none"
                            data-testid={`edit-threshold-${size}`}
                          />
                        </div>
                      </LockedFeature>
                    </div>
                  ))}
                </div>
              </Field>
            )}

            <LockedFeature
              locked={linkOutLocked}
              planLabel="Creator"
              message="Add a preview or demo link — shown as a “👁 Preview” button next to Select"
              onUpgrade={goUpgrade}
            >
              <Field
                label="Sample / demo link"
                hint={hasBuyLinkValue ? "clear the Buy link below to use this instead" : "optional · adds a “👁 Preview” button next to Select"}
              >
                <input
                  type="text"
                  inputMode="url"
                  value={form.preview_url || ""}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.trim() && (form.external_url || "").trim()) {
                      setForm((f) => ({ ...f, preview_url: val, external_url: "" }));
                    } else {
                      set("preview_url", val);
                    }
                  }}
                  maxLength={MAX_LINK_URL}
                  placeholder="https://… e-book sample, course trailer, demo video"
                  className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none disabled:cursor-not-allowed disabled:bg-ink/5 disabled:text-ink/30"
                  data-testid="edit-preview-url"
                />
                {previewLinkErr && <p className="mt-1 text-xs font-medium text-hotpink">{previewLinkErr}</p>}
                {hasBuyLinkValue && (
                  <p className="mt-1 text-xs font-medium text-ink/40">A product can use a Preview link or a Buy link, not both.</p>
                )}
              </Field>
            </LockedFeature>

            {(
              <LockedFeature
                locked={linkOutLocked}
                planLabel="Creator"
                message="Sell affiliate or external-link products — customers tap straight to your link"
                onUpgrade={goUpgrade}
              >
                <div className="space-y-3 rounded-2xl border border-ink/10 bg-white/60 p-4">
                  <p className="text-xs text-ink/60">
                    <strong className="text-ink">Affiliate / External Link.</strong> Adds a Buy button to the DM card. By default it
                    sits next to Select. To hide Select, choose “Send them to my affiliate link” below. Leave blank for a normal product.
                  </p>
                  <Field
                    label="Buy link"
                    hint={hasPreviewValue ? "clear the Preview link above to use this instead" : "optional · https:// only"}
                  >
                    <input
                      type="text"
                      inputMode="url"
                      value={form.external_url || ""}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val.trim() && (form.preview_url || "").trim()) {
                          setForm((f) => ({ ...f, external_url: val, preview_url: "" }));
                        } else {
                          set("external_url", val);
                        }
                      }}
                      maxLength={MAX_LINK_URL}
                      placeholder="https://your-affiliate-link"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none disabled:cursor-not-allowed disabled:bg-ink/5 disabled:text-ink/30"
                      data-testid="edit-external-url"
                    />
                    {buyLinkErr && <p className="mt-1 text-xs font-medium text-hotpink">{buyLinkErr}</p>}
                  </Field>
                  <Field label="Button label" hint={`optional · ${(form.external_label || "").length}/${MAX_LINK_LABEL}`}>
                    <input
                      value={form.external_label || ""}
                      onChange={(e) => set("external_label", e.target.value)}
                      maxLength={MAX_LINK_LABEL}
                      disabled={hasPreviewValue}
                      placeholder="🛒 Shop now"
                      className="w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none disabled:cursor-not-allowed disabled:bg-ink/5 disabled:text-ink/30"
                      data-testid="edit-external-label"
                    />
                  </Field>
                  {hasPreviewValue && (
                    <p className="text-xs font-medium text-ink/40">A product can use a Preview link or a Buy link, not both.</p>
                  )}
                </div>
              </LockedFeature>
            )}

            {!linkOutLocked && (() => {
              const instantOk = !!(
                form.is_digital &&
                String(form.price).trim() !== "" &&
                Number(form.price) === 0 &&
                (form.digital_delivery_url || "").trim()
              );
              const buyLabel = (form.external_label || "").trim() || "🛒 Shop now";
              const current = form.dm_action || "select";
              const dmOptions = [
                {
                  value: "select",
                  title: "Normal checkout in the DM",
                  desc: "Customer taps Select, then enters details and pays inside the chat. An order is created. Best for most products.",
                  buttons: ["👉 Select #1"].concat(hasBuyLinkValue ? [buyLabel] : hasPreviewValue ? ["👁 Preview"] : []),
                  disabled: false,
                  reason: "",
                },
                {
                  value: "link_only",
                  title: "Send them to my affiliate link",
                  desc: "No Select button. The card shows your Buy button (plus a Visit Store button if your storefront is enabled), so the customer shops or pays on your own page or affiliate link. No order is created here.",
                  buttons: [buyLabel],
                  disabled: !hasBuyLinkValue,
                  reason: "Add a Buy link above to use this.",
                },
                {
                  value: "instant",
                  title: "Free instant download",
                  desc: "One tap on Get it free and the delivery link is sent straight away. No name, email or payment asked. Each customer can claim it once. A Visit Store button also shows if your storefront is enabled.",
                  buttons: ["🎁 Get it free"],
                  disabled: !instantOk,
                  reason: "Needs a digital product, priced at 0, with a delivery link.",
                },
              ];
              return (
                <div className="space-y-2 rounded-2xl border border-ink/10 bg-white/60 p-4" data-testid="edit-dm-action">
                  <p className="text-xs text-ink/60">
                    <strong className="text-ink">What should the Instagram DM card do when someone taps this product?</strong>
                  </p>
                  <div className="space-y-2">
                    {dmOptions.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        disabled={opt.disabled}
                        onClick={() => set("dm_action", opt.value)}
                        data-testid={`edit-dm-action-${opt.value}`}
                        className={`flex w-full items-start gap-3 rounded-2xl border-2 p-3 text-left transition ${
                          current === opt.value
                            ? "border-hotpink bg-hotpink/10 ring-2 ring-hotpink/25"
                            : "border-ink/10 bg-white/60"
                        } ${opt.disabled ? "cursor-not-allowed opacity-50" : "hover:border-hotpink/60 hover:bg-hotpink/5"}`}
                      >
                        <div className="w-28 flex-none overflow-hidden rounded-xl border border-ink/10 bg-white">
                          <div className="h-10 bg-ink/10" />
                          <div className="space-y-1 p-1.5">
                            <div className="h-1.5 w-3/4 rounded bg-ink/20" />
                            {opt.buttons.map((b) => (
                              <div key={b} className="truncate rounded-md border border-ink/15 px-1 py-0.5 text-center text-[9px] font-semibold text-ink/70">
                                {b}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-ink">{opt.title}</p>
                          <p className="mt-0.5 text-xs text-ink/60">{opt.desc}</p>
                          {opt.disabled && <p className="mt-1 text-xs font-medium text-hotpink">{opt.reason}</p>}
                        </div>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-ink/50">
                    DM cards you already sent keep their old buttons. The new setting applies to cards customers see from now on.
                  </p>
                </div>
              );
            })()}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-ink/10 bg-white px-6 py-4">
          <p className="text-xs text-ink/50">Changes apply instantly to live DM checkouts.</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-ink/15 bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-ink/5"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="edit-save"
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-ink px-5 py-2 text-sm font-semibold text-cream hover:bg-ink/85 disabled:opacity-50"
            >
              <Check className="h-4 w-4" /> {saving ? "Saving…" : isCreate ? "Add product" : "Save product"}
            </button>
          </div>
        </div>
      </motion.form>
    </motion.div>
  );
}

// One of the 3 optional Pro gallery slots — shows a filled thumbnail with a
// remove (X), or an empty "+" tile that opens a file picker. Deliberately
// tiny and un-fussy: this is a nice-to-have, not the main photo flow.
function AdditionalImageSlot({ slot, onPick, onRemove, onError }) {
  const inputRef = useRef(null);

  const handleFile = (f) => {
    if (!f) return;
    if (f.type !== "image/jpeg") {
      onError("Only JPG images are allowed.");
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      onError("Image must be under 1 MB. Please compress it and try again.");
      return;
    }
    onPick(f);
  };

  if (slot) {
    return (
      <div className="group relative aspect-square overflow-hidden rounded-xl bg-ink/5">
        <img src={slot.url} alt="" className="h-full w-full object-cover" />
        <button
          type="button"
          onClick={onRemove}
          className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-ink/70 text-white opacity-0 transition group-hover:opacity-100"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => inputRef.current?.click()}
      className="grid aspect-square place-items-center rounded-xl border-2 border-dashed border-ink/15 bg-ink/[0.02] text-ink/30 hover:border-hotpink hover:text-hotpink"
    >
      <Plus className="h-4 w-4" />
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = "";
        }}
      />
    </button>
  );
}

// A gallery slot beyond the merchant's plan limit — shown as a locked tile
// with a lock icon instead of the "+" add button, so Starter/Growth
// merchants can see exactly how many more photo slots Growth/Pro unlock.
function LockedImageSlot({ onUpgrade }) {
  return (
    <button
      type="button"
      onClick={onUpgrade}
      className="grid aspect-square place-items-center rounded-xl border-2 border-dashed border-ink/10 bg-ink/[0.03] text-ink/25 hover:border-hotpink hover:text-hotpink"
      data-testid="additional-image-slot-locked"
    >
      <Lock className="h-4 w-4" />
    </button>
  );
}

// -------------------- Confirm dialog ----------------------------------------
function ConfirmDialog({ title, body, confirmLabel, onConfirm, onCancel }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onCancel}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/60 p-6"
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 20, opacity: 0 }}
        className="w-full max-w-md rounded-3xl bg-cream p-6"
      >
        <div className="mb-4 flex items-center gap-3">
          <div className="grid h-10 w-10 place-items-center rounded-full bg-hotpink/10 text-hotpink">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <h3 className="text-lg font-bold text-ink">{title}</h3>
        </div>
        <p className="text-sm text-ink/70">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="rounded-full border border-ink/15 bg-white px-4 py-2 text-sm font-semibold text-ink hover:bg-ink/5"
          >
            Keep it
          </button>
          <button
            onClick={onConfirm}
            data-testid="confirm-delete-yes"
            className="rounded-full bg-hotpink px-5 py-2 text-sm font-semibold text-white hover:bg-hotpink/90"
          >
            {confirmLabel}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// -------------------- Existing category / color hints -----------------------
function normalizeTerm(s) {
  return (s || "").toLowerCase().replace(/[^\p{L}\p{N}]+/gu, " ").trim();
}

function withinOneEdit(a, b) {
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (a.length > b.length) i += 1;
    else if (a.length < b.length) j += 1;
    else {
      i += 1;
      j += 1;
    }
  }
  return edits + (a.length - i) + (b.length - j) <= 1;
}

// Same word ignoring case/punctuation, a plural ("Prompt"/"Prompts"),
// or a one-letter typo on longer words ("Colour"/"Color").
function isSimilarTerm(a, b) {
  const x = normalizeTerm(a);
  const y = normalizeTerm(b);
  if (!x || !y) return false;
  if (x === y) return true;
  if (x + "s" === y || y + "s" === x || x + "es" === y || y + "es" === x) return true;
  return Math.min(x.length, y.length) >= 6 && withinOneEdit(x, y);
}

// Distinct non-empty values (case-insensitive), most-used first, using the
// most common spelling of each.
function collectDistinct(values) {
  const groups = new Map();
  values.forEach((raw) => {
    const v = (raw || "").trim();
    if (!v) return;
    const key = v.toLowerCase();
    const g = groups.get(key) || { counts: new Map(), total: 0 };
    g.counts.set(v, (g.counts.get(v) || 0) + 1);
    g.total += 1;
    groups.set(key, g);
  });
  return Array.from(groups.values())
    .map((g) => ({
      label: Array.from(g.counts.entries()).sort((a, b) => b[1] - a[1])[0][0],
      total: g.total,
    }))
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label))
    .map((g) => g.label);
}

// Up to this many existing values are shown as clickable chips; more than
// this switches to a compact dropdown so the form never gets tall.
const INLINE_CHIP_LIMIT = 4;

// One field for typing a new value or picking an existing one.
// Few values -> chips under the box; many -> arrow on the right opens a list.
function SuggestInput({ noun, nounPlural, value, onChange, options = [], maxLength, placeholder, testId, inputTestId }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => {
      document.removeEventListener("mousedown", onOutside);
      document.removeEventListener("touchstart", onOutside);
    };
  }, [open]);

  const typed = (value || "").trim();
  const similar = typed ? options.find((o) => o !== typed && isSimilarTerm(o, typed)) : null;
  const useDropdown = options.length > INLINE_CHIP_LIMIT;
  const sorted = useDropdown ? [...options].sort((a, b) => a.localeCompare(b)) : options;
  // Narrow the list while typing a new value; show everything when the box is
  // empty or already holds an existing value.
  const filtered =
    typed && !options.includes(typed)
      ? sorted.filter((o) => o.toLowerCase().includes(typed.toLowerCase()))
      : sorted;
  const pick = (v) => {
    onChange(v);
    setOpen(false);
  };

  return (
    <div data-testid={testId}>
      <div ref={wrapRef} className="relative">
        <input
          value={value || ""}
          onChange={(e) => {
            onChange(e.target.value);
            if (useDropdown) setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") setOpen(false);
          }}
          maxLength={maxLength}
          placeholder={placeholder}
          className={`w-full rounded-2xl border border-ink/15 bg-white px-4 py-2.5 text-sm focus:border-hotpink focus:outline-none ${
            useDropdown ? "pr-11" : ""
          }`}
          data-testid={inputTestId}
        />
        {useDropdown && (
          <>
            <button
              type="button"
              aria-label={`Show existing ${nounPlural}`}
              onClick={() => setOpen((o) => !o)}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1.5 text-ink/50 transition-colors hover:text-hotpink"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
              <ul
                role="listbox"
                className="absolute z-30 mt-1 max-h-52 w-full overflow-auto rounded-2xl border border-ink/15 bg-white py-1 shadow-lg"
              >
                {filtered.length === 0 ? (
                  <li className="px-4 py-2 text-xs text-ink/50">
                    No match. This will be saved as a new {noun}.
                  </li>
                ) : (
                  filtered.map((o) => (
                    <li key={o}>
                      <button
                        type="button"
                        onClick={() => pick(o)}
                        className={`block w-full px-4 py-2 text-left text-sm transition-colors hover:bg-hotpink/5 ${
                          o === typed ? "bg-hotpink/10 font-semibold text-hotpink" : "text-ink"
                        }`}
                      >
                        {o}
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </>
        )}
      </div>

      {similar && (
        <p className="mt-2 flex items-start gap-1.5 text-xs font-semibold text-amber-700">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            You already have a similar {noun}:{" "}
            <button type="button" onClick={() => pick(similar)} className="underline underline-offset-2">
              {similar}
            </button>
            . Use it to avoid duplicates?
          </span>
        </p>
      )}

      {!useDropdown && options.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[11px] font-medium text-ink/50">Existing {nounPlural}:</span>
          {options.map((o) => (
            <button
              key={o}
              type="button"
              onClick={() => pick(o)}
              className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                o === typed
                  ? "border-hotpink bg-hotpink/10 text-hotpink"
                  : "border-ink/15 bg-white text-ink/70 hover:border-hotpink hover:text-hotpink"
              }`}
            >
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// -------------------- Field label wrapper -----------------------------------
function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-baseline justify-between text-xs font-semibold uppercase tracking-wider text-ink/70">
        <span>
          {label}
          {required && <span className="ml-1 text-hotpink">*</span>}
        </span>
        {hint && <span className="text-[10px] font-medium normal-case text-ink/40">{hint}</span>}
      </span>
      {children}
    </label>
  );
}
